import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { debounceTime, merge, Subject, takeUntil } from 'rxjs';
import { IFormKey } from 'src/app/modules/shared/models/forms';
import { DATE_FORMAT } from 'src/app/modules/shared/models/policy.model';
import { LoggerService } from 'src/app/services/logger.service';
import { CommonUtils } from 'src/app/utils/CommonUtils';
import { FormUtils } from 'src/app/utils/FormUtils';
import { advanced_search_form_keys, advanced_search_form_template, advanced_search_form_validators, ISearchEvent, search_form_keys, simple_search_form_template, simple_search_form_validators } from './search-form.constants';
import moment = require('moment');

@Component({
  selector: 'app-search-form',
  templateUrl: './search-form.component.html',
  styleUrl: './search-form.component.scss',
})
export class SearchFormComponent implements OnInit, OnDestroy {

  @Input('event')
    set _event(val: ISearchEvent) {
      if (val) {
        this.manageEvent(val);
      }
    }

  event: ISearchEvent;
  simpleSearchForm: FormGroup;
  simpleSearchKeys: IFormKey[] = CommonUtils.assign(search_form_keys);
  advancedSearchForm: FormGroup;
  advancedSearchKeys: IFormKey[] = CommonUtils.assign(advanced_search_form_keys);
  currentTab = 0;
  destroy$ = new Subject();

  @Output() tabChanged = new EventEmitter<number>();
  @Output() searchRequested = new EventEmitter<ISearchEvent>();
  @Output() clearSearchResults = new EventEmitter();

  ngOnInit(): void {
    this.createForm();
    this.watchForm();
  }

  get searchButtonDisabled(): boolean {
    const formValues = this.currentTab ? this.advancedSearchForm.value : this.simpleSearchForm.value;
    const atLeastOneFieldFilled = Object.entries(formValues).some(([key, value]) => value && value !== '' && key !== 'limit');
    return !atLeastOneFieldFilled || (this.isAdvancedSearch ? !this.advancedSearchForm.valid : !this.simpleSearchForm.valid);
  }

  get isAdvancedSearch(): boolean {
    return this.currentTab === 1;
  }

  get onlyPolicyNumber(): boolean {
    if (this.advancedSearchForm) {
      const isPolicyNumberPopulated: boolean = this.advancedSearchForm.get('policyId').value !== '';
      const anyOtherFieldPopulated: boolean = Object.entries(this.advancedSearchForm.value).some(([key, value]) =>
        value && value !== '' &&
        key !== 'limit' &&
        key !== 'policyId' &&
        key !== 'contractId'
      );
      return isPolicyNumberPopulated && !anyOtherFieldPopulated;
    }
    return false;
  }

  get onlyContractNumber(): boolean {
    if (this.advancedSearchForm) {
      const isContractNumberPopulated: boolean = this.advancedSearchForm.get('contractId').value !== '';
      const anyOtherFieldPopulated: boolean = Object.entries(this.advancedSearchForm.value).some(([key, value]) =>
        value && value !== '' &&
        key !== 'limit' &&
        key !== 'contractId' &&
        key !== 'policyId');
      return isContractNumberPopulated && !anyOtherFieldPopulated;
    }
    return false;
  }

  manageEvent(inputEvent: ISearchEvent) {
    this.event = inputEvent;
  }

  createForm() {
    this.simpleSearchForm = FormUtils.initFormGroupFromTemplate(simple_search_form_template, simple_search_form_validators);
    this.advancedSearchForm = FormUtils.initFormGroupFromTemplate(advanced_search_form_template, advanced_search_form_validators);
    if (this.event) {
      this.currentTab = this.event.type === 'ADVANCED' ? 1 : 0;
      if (this.isAdvancedSearch) {
        this.advancedSearchForm.setValue(this.event.rawForm);
      } else {
        this.simpleSearchForm.setValue(this.event.rawForm);
      }
    }
  }

  watchForm() {
    merge(
      this.simpleSearchForm.valueChanges,
      this.advancedSearchForm.valueChanges
    )
      .pipe(
        debounceTime(350),
        takeUntil(this.destroy$)
      )
      .subscribe(value => {
        LoggerService.logInfo(this.constructor.name, 'watchForm', { value });
      })
  }

  onTabChange(event) {
    LoggerService.logInfo(this.constructor.name, 'onTabChange', { index: event?.index });
    this.currentTab = event?.index;
    this.tabChanged.emit(this.currentTab)
  }

  reset() {
    if (this.currentTab) {
      this.advancedSearchForm.reset();
      this.advancedSearchForm = FormUtils.initFormGroupFromTemplate(advanced_search_form_template, advanced_search_form_validators);
    } else {
      this.simpleSearchForm.reset();
    }
    this.event = CommonUtils.assign(null);
    this.clearSearchResults.emit();
  }

  search() {
    LoggerService.logInfo(this.constructor.name, 'search', { index: this.currentTab });
    let rawForm = this.isAdvancedSearch ? this.advancedSearchForm.getRawValue() : this.simpleSearchForm.getRawValue();
    const type = this.isAdvancedSearch ? 'ADVANCED' : 'BASIC';
    rawForm = this.formatFormRawValue(rawForm, type);
    this.searchRequested.emit({
      type: type,
      rawForm: rawForm,
      skipSubjectList: this.onlyPolicyNumber || this.onlyContractNumber,
      skipList: true
    })
  }

  formatFormRawValue(rawValue: any, type: string) {
    if (rawValue) {
      Object.keys(rawValue).forEach(key =>
        rawValue[key] = CommonUtils.isVoid(rawValue[key]) ? null : (typeof rawValue[key] === 'string' ? rawValue[key].replace(/\s+/g, ' ').trim() : rawValue[key])
      )
      if (type === 'ADVANCED') {
        rawValue['birthDate'] = rawValue['birthDate'] ? moment(rawValue['birthDate']).format(DATE_FORMAT) : null;
      }
    }
    return rawValue;
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}
